import { useCallback, useState } from 'react'

import { useMutation, useQuery } from '@apollo/client'

import {
  Flex,
  Heading,
  Separator,
  Switch,
  Text,
  toast,
} from '@buffer-mono/popcorn'

import { graphql } from '~publish/gql'
import type { EmailNotificationPreference } from '~publish/gql/graphql'

import EmailNotificationsSkeleton from './components/EmailNotificationsSkeleton'
import { NewSettingsPageLayout } from './components/NewSettingsPageLayout'

import style from './Settings.module.css'

export const GetEmailNotificationPreferencesQuery = graphql(`
  query getEmailNotificationPreferencesQuery {
    emailNotificationPreferences {
      __typename
      ... on EmailNotificationPreferencesResponse {
        preferences {
          id
          name
          description
          subscribed
          cioTopicId
        }
      }
      ... on EmailNotificationPreferenceError {
        message
      }
    }
  }
`)

export const UpdateEmailNotificationPreferencesMutation = graphql(`
  mutation emailNotificationPreferences(
    $input: [EmailNotificationPreferenceInput!]!
  ) {
    emailNotificationPreferences(input: $input) {
      __typename
      ... on EmailNotificationPreferencesResponse {
        preferences {
          id
          name
          description
          subscribed
          cioTopicId
        }
      }
      ... on EmailNotificationPreferenceError {
        message
      }
    }
  }
`)

type NotificationOptionProps = {
  id: string
  subscribed: boolean
}

const ACCOUNT_ACTIVITIES_GROUP = [
  'dailyPostRecap',
  'weeklyAnalyticsReport',
  'postPublished',
  'postFailure',
  'queue',
  'reminders',
  'collaboration',
  'channelConnectionUpdates',
  'billing',
]
const FROM_BUFFER_GROUP = [
  'gettingStarted',
  'socialPostPromptsAndIdeas',
  'userFeedbackAndResearch',
  'productUpdatesAndNews',
  'newsletter',
  'openBlogNewsletter',
  'bufferCareers',
]

// Filter and sort the preferences based on the order of the GROUP array
function filterAndSortPreferences(
  preferences: EmailNotificationPreference[],
  orderArray: string[],
): EmailNotificationPreference[] {
  return preferences
    ?.filter((preference) => orderArray.includes(preference.id))
    .sort((a, b) => orderArray.indexOf(a.id) - orderArray.indexOf(b.id))
}

const NotificationOption = ({
  preferenceData,
  updateNotificationSetting,
}: {
  preferenceData: EmailNotificationPreference
  updateNotificationSetting: (
    options: NotificationOptionProps,
  ) => void | Promise<void>
}): JSX.Element => {
  const { id, subscribed, name, description } = preferenceData
  const [isOn, setIsOn] = useState(subscribed)

  function handleOnSwitchChange(value: boolean): void {
    setIsOn(value)
    void updateNotificationSetting({ id, subscribed: value })
  }

  return (
    <div className={style.fullWidth}>
      <Flex
        gap="md"
        align="center"
        justify="between"
        className={style.fullWidth}
      >
        <Flex direction="column" gap="2xs">
          <Heading as="h2" size="xsmall" id={`${id}-heading`}>
            {name}
          </Heading>
          <Text id={`${id}-desc`} style={{ textWrap: 'pretty' }}>
            {description}
          </Text>
        </Flex>
        <Switch
          id={id}
          onCheckedChange={handleOnSwitchChange}
          checked={isOn}
          aria-labelledby={`${id}-heading`}
          aria-describedby={`${id}-desc`}
        />
      </Flex>
    </div>
  )
}

const EmailNotifications = (): JSX.Element => {
  const { data } = useQuery(GetEmailNotificationPreferencesQuery)
  const [updateEmailNotificationPreferences] = useMutation(
    UpdateEmailNotificationPreferencesMutation,
  )

  const updateNotificationSetting = useCallback(
    async (options: NotificationOptionProps): Promise<void> => {
      try {
        await updateEmailNotificationPreferences({
          variables: {
            input: {
              id: options.id,
              subscribed: options.subscribed,
            },
          },
        })
      } catch {
        toast.error(
          'Sorry! There was an issue updating your email preferences. Please try again.',
        )
      }
    },
    [updateEmailNotificationPreferences],
  )

  const preferencesList =
    data?.emailNotificationPreferences.__typename ===
    'EmailNotificationPreferencesResponse'
      ? data?.emailNotificationPreferences.preferences
      : null

  const accountActivitiesPreferences = filterAndSortPreferences(
    preferencesList ?? [],
    ACCOUNT_ACTIVITIES_GROUP,
  ).sort(
    (a, b) =>
      ACCOUNT_ACTIVITIES_GROUP.indexOf(a.id) -
      ACCOUNT_ACTIVITIES_GROUP.indexOf(b.id),
  )

  const fromBufferPreferences = filterAndSortPreferences(
    preferencesList ?? [],
    FROM_BUFFER_GROUP,
  )

  if (!preferencesList) {
    return (
      <NewSettingsPageLayout
        title="Notifications"
        testId="email-notifications-skeleton"
      >
        {ACCOUNT_ACTIVITIES_GROUP.map((option) => (
          <EmailNotificationsSkeleton key={option} />
        ))}
        {FROM_BUFFER_GROUP.map((option) => (
          <EmailNotificationsSkeleton key={option} />
        ))}
      </NewSettingsPageLayout>
    )
  }

  return (
    <NewSettingsPageLayout
      title="Notifications"
      gap="xl"
      testId="email-notifications-page"
    >
      <Heading as="h2" size="small">
        Account activities
      </Heading>

      {accountActivitiesPreferences?.map((emailNotificationPreference) => (
        <NotificationOption
          key={emailNotificationPreference.id}
          preferenceData={emailNotificationPreference}
          updateNotificationSetting={updateNotificationSetting}
        />
      ))}

      <Separator />

      <Heading as="h2" size="small">
        From Buffer
      </Heading>

      {fromBufferPreferences?.map((emailNotificationPreference) => (
        <NotificationOption
          key={emailNotificationPreference.id}
          preferenceData={emailNotificationPreference}
          updateNotificationSetting={updateNotificationSetting}
        />
      ))}
    </NewSettingsPageLayout>
  )
}

export default EmailNotifications
